import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CaseTimelineResponse,
  CaseView,
  CloseCaseBody,
  CloseReason,
  FunnelReport,
  ReferralCategory,
} from '@tex/finance-shared'
import { API_URL, ApiError, apiGet, apiSend, stepUpPath } from './api'
import { caseKeys } from './cases'

const cases = '/api/v1/cases'

export const operationKeys = {
  timeline: (caseId: string) => ['timeline', caseId] as const,
  funnel: (from: string, to: string, dealerId: string | null) => ['reports', 'funnel', from, to, dealerId ?? 'all'] as const,
}

export const CLOSE_REASON_LABELS: Record<CloseReason, string> = {
  borrower_withdrew: 'Borrower withdrew',
  dealer_withdrew: 'Dealer withdrew',
  no_offer: 'No offer',
  out_of_scope_referred: 'Out of scope — referred elsewhere',
  duplicate: 'Duplicate request',
  expired: 'Expired',
  declined_by_partner: 'Declined by partner',
  other: 'Other',
}

export const REFERRAL_CATEGORY_LABELS: Record<ReferralCategory, string> = {
  cash_out: 'Cash-out against owned equipment',
  debt_restructuring: 'Debt restructuring',
  receivables: 'Receivables / working capital',
  robot_deployment: 'Robot / automation deployment',
  venture_equipment: 'Venture-backed equipment',
  private_party: 'Private-party sale',
  other: 'Other',
}

/** Every event on the case the caller may see; the server filters by audience and strips metadata for non-Tex readers. */
export function useCaseTimeline(caseId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: operationKeys.timeline(caseId),
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiGet<CaseTimelineResponse>(`${cases}/${caseId}/timeline${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ''}`),
    getNextPageParam: (last) => last.next_cursor,
  })
}

export function useCloseCase(caseId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CloseCaseBody) => apiSend<CaseView>('POST', `${cases}/${caseId}/close`, body),
    onSuccess: (view) => {
      qc.setQueryData(caseKeys.detail(caseId), view)
      return Promise.all([
        qc.invalidateQueries({ queryKey: ['cases', 'list'] }),
        qc.invalidateQueries({ queryKey: ['work-queue'] }),
        qc.invalidateQueries({ queryKey: operationKeys.timeline(caseId) }),
      ])
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'version_conflict') {
        return qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) })
      }
    },
  })
}

export interface ReportWindowInput {
  /** ISO calendar dates (YYYY-MM-DD); `to` is inclusive of that day. */
  from: string
  to: string
  dealerId: string | null
}

/** A `Date` as the local calendar day (`YYYY-MM-DD`) for a date input; the inverse of `windowBounds`. */
export function localDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Start of `from` and end of `to`, in the browser's zone, as ISO instants for the API. */
export function windowBounds(w: ReportWindowInput): { from: string; to: string } {
  const start = new Date(`${w.from}T00:00:00`)
  const end = new Date(`${w.to}T00:00:00`)
  end.setDate(end.getDate() + 1)
  return { from: start.toISOString(), to: end.toISOString() }
}

function reportQuery(w: ReportWindowInput): string {
  const b = windowBounds(w)
  const qs = new URLSearchParams({ from: b.from, to: b.to })
  if (w.dealerId) qs.set('dealer_id', w.dealerId)
  return qs.toString()
}

export function useFunnelReport(w: ReportWindowInput, enabled = true) {
  return useQuery({
    queryKey: operationKeys.funnel(w.from, w.to, w.dealerId),
    enabled,
    queryFn: () => apiGet<FunnelReport>(`/api/v1/reports/funnel?${reportQuery(w)}`),
  })
}

export interface CsvDownload {
  rows: number
  truncated: boolean
  filename: string
}

/**
 * Fetches the audited case-events CSV and hands it to the browser as a download. A stale
 * session gets the same step-up redirect as any other privileged call.
 */
export async function downloadCaseEventsCsv(w: ReportWindowInput): Promise<CsvDownload> {
  const path = `/api/v1/reports/case-events.csv?${reportQuery(w)}`
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' })
  if (!res.ok) {
    let code = `http_${res.status}`
    let reason: string | undefined
    try {
      const body = (await res.json()) as { error?: string; reason?: string }
      code = body.error ?? code
      reason = body.reason
    } catch {
      // non-JSON body
    }
    if (res.status === 403 && code === 'step_up_required') {
      window.location.assign(stepUpPath())
      throw new ApiError(403, code, 'Confirm it is you to continue: signing in again…')
    }
    throw new ApiError(res.status, code, reason)
  }
  const blob = await res.blob()
  const disposition = res.headers.get('content-disposition') ?? ''
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'tex-case-events.csv'
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return {
    rows: Number(res.headers.get('x-row-count') ?? '0'),
    truncated: res.headers.get('x-truncated') === 'true',
    filename,
  }
}
